import React, { useEffect, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { Row, Col } from 'react-bootstrap';

import './index.scss';

import 'react-datepicker/dist/react-datepicker.css';

const ranges = [
    {
        label: "0–5",
        range: [0, 5],
        weeks: [
            { week: "January", date: "Jan 6, 2024" },
            { week: "February", date: "Feb 14, 2024" },
            { week: "March", date: "Mar 20, 2024" },
            { week: "April", date: "Apr 26, 2024" },
        ],
    },
    {
        label: "5–10",
        range: [5, 10],
        weeks: [
            { week: "May", date: "May 1, 2024" },
            { week: "June", date: "Jun 8, 2024" },
            { week: "July", date: "Jul 15, 2024" },
            { week: "August", date: "Aug 22, 2024" },
        ],
    },
    {
        label: "10–15",
        range: [10, 15],
        weeks: [
            { week: "September", date: "Sep 5, 2025" },
            { week: "October", date: "Oct 12, 2025" },
            { week: "November", date: "Nov 19, 2025" },
            { week: "December", date: "Dec 26, 2025" },
        ],
    },
];

const Yearly = ({chartData}) => {

    const [activeRange, setActiveRange] = useState(ranges[0].range);
    const [activeWeeks, setActiveWeeks] = useState(ranges[0].weeks);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {

        const data = chartData

        const bubbleChartId = "bubble-chartdiv-yearly";

        const existingRoot = am5.registry.rootElements.find(
            (root) => root.dom && root.dom.id === bubbleChartId
        );
        if (existingRoot) {
            existingRoot.dispose();
        }

        const root = am5.Root.new(bubbleChartId);

        root.setThemes([am5themes_Animated.new(root)]);

        // Aggregate data by category
        const aggregatedData = data.reduce((acc, item) => {
            // Create a composite key using type and category
            const key = `${item.type}-${item.category}`;
            if (!acc[key]) acc[key] = {};
            if (!acc[key][item.name]) acc[key][item.name] = { value: 0, color: item.color, opacity: item.fillOpacity };
        
            // Add value and keep the color
            acc[key][item.name].value += item.value;
            return acc;
        }, {});

        // Add 'details' field to each data item
        data.forEach((item) => {
            // Use the composite key to access aggregated data
            const key = `${item.type}-${item.category}`;
            const categoryData = aggregatedData[key] || {};
        
            // Generate details
            item.details = Object.entries(categoryData)
                .map(([key, data]) => `${key}: ${data.value}, color: ${data.color}, opacity:${data.opacity}`)
                .join("\n");
        
            // Generate dynamic HTML
            item.htmlDetails = `
                <div class="custom-tooltip-container">
                    <div class="tooltip-heading"><h2>${item.title}<h2></div>
                    ${Object.entries(categoryData)
                        .map(
                            ([key, data]) => `
                            <div class="d-flex align-items-center w-100">
                                <span class="tooltip-dot d-inline-block" style=" background-color: ${data.color};opacity: ${data.opacity}"></span>
                                <span class="tooltip-value d-inline-block">${data.value}</span> 
                                <span class="tooltip-key d-inline-block">${key}</span>
                            </div>`
                        )
                        .join("")}
                </div>`;
        });

        const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelY: "none",
                pinchZoomX: false,
                pinchZoomY: false,
            })
        );

        root._logo.dispose();

        const xAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
                min: activeRange[0],
                max: activeRange[1],
                strictMinMax: true,
                renderer: am5xy.AxisRendererX.new(root, {}),
                // tooltip: am5.Tooltip.new(root, {}),
            })
        );

        xAxis.get("renderer").grid.template.set("visible", false);
        xAxis.get("renderer").labels.template.set("visible", false);

        const yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                min: 0,
                max: 10,
                strictMinMax: true,
                renderer: am5xy.AxisRendererY.new(root, {}),
                // tooltip: am5.Tooltip.new(root, {}),
            })
        );

        yAxis.get("renderer").grid.template.set("visible", false);
        yAxis.get("renderer").labels.template.set("visible", false);

        const series = chart.series.push(
            am5xy.LineSeries.new(root, {
                calculateAggregates: true,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "y",
                valueXField: "x",
                valueField: "value",
                seriesTooltipTarget: "bullet",
                tooltip: am5.Tooltip.new(root, {
                    pointerOrientation: "horizontal",
                    html: "{htmlDetails}", // Use your custom HTML
                    getFillFromSprite: false,
                    background: am5.PointedRectangle.new(root, {
                        fill: am5.color(0xffffff), // Fixed white background
                        fillOpacity: 1,           // Fully opaque
                        stroke: am5.color(0xFFFFFF), // Border color
                        strokeWidth: 1,           // Border width
                        pointerBaseWidth: 10,      // Reduce pointer base width
                        pointerLength: 8,         // Reduce pointer length to minimize space
                        cornerRadius: 10,          // Optional rounded corners
                        shadowColor: am5.color(0xeaf1ff), // Shadow color
                        shadowBlur: 40.9,                 // Amount of blur for the shadow
                        shadowOffsetX: 5,               // Horizontal shadow offset
                        shadowOffsetY: 5,
                        height: 108
                    }),
                }),
            })
        );

        series.strokes.template.set("visible", false);

        const circleTemplate = am5.Template.new({});
        circleTemplate.adapters.add("fill", (fill, target) => {
            const dataItem = target.dataItem;
            if (dataItem && dataItem.dataContext && dataItem.dataContext.color) {
                return am5.Color.fromString(dataItem.dataContext.color);
            }
            return fill || am5.Color.fromString("#ccc");
        });

        circleTemplate.adapters.add("fillOpacity", (opacity, target) => {
            const dataItem = target.dataItem;
            if (dataItem && dataItem.dataContext && dataItem.dataContext.fillOpacity !== undefined) {
                return dataItem.dataContext.fillOpacity; // Dynamic fill opacity from data
            }
            return opacity || 1; // Default opacity (fully opaque)
        });
                
            // Adapter for dynamic stroke width
        circleTemplate.adapters.add("strokeWidth", (width, target) => {
            const dataItem = target.dataItem;
            if (dataItem && dataItem.dataContext && dataItem.dataContext.strokeWidth !== undefined) {
                return dataItem.dataContext.strokeWidth; // Dynamic stroke width from data
            }
            return width || 1; // Default stroke width
        });
                
            // Adapter for stroke color (dynamic border color)
        circleTemplate.adapters.add("strokeColor", (stroke, target) => {
            const dataItem = target.dataItem;
            if (dataItem && dataItem.dataContext && dataItem.dataContext.strokeColor) {
            return am5.Color.fromString(dataItem.dataContext.strokeColor); // Border color
                }
            return stroke || am5.Color.fromString("#000"); // Default border color
        });

        // Adapter to control visibility based on the value
        circleTemplate.adapters.add("visible", (visible, target) => {
            const dataItem = target.dataItem;
            if (dataItem && dataItem.dataContext && dataItem.dataContext.value === 0) {
                return false; // Hide circle if value is 0
            }
            return true; // Default to visible
        });

        series.bullets.push(() =>
            am5.Bullet.new(root, {
                sprite: am5.Circle.new(
                    root,
                    {
                        radius: 50,
                        fill: series.get("fill"),
                        fillOpacity: series.get("fillOpacity"),
                        stroke: series.get("strokeColor"),
                        strokeWidth: series.get("strokeWidth"),
                        strokeOpacity: series.get("fillOpacity")
                    },
                    circleTemplate
                ),
            })
        );

        series.set("heatRules", [
            {
                target: circleTemplate,
                min: 10, // Absolute minimum radius for value = 0
                max: 40, // Maximum radius
                dataField: "value", // Field used for heat calculations
                key: "radius", // Attribute to apply the rule
            },
        ]);

        series.data.setAll(data);

        chart.set(
            "cursor",
            am5xy.XYCursor.new(root, {
                xAxis: xAxis,
                yAxis: yAxis,
                snapToSeries: [series],
                behaviors: [],
            })
        );

        const cursor = chart.get("cursor");
        cursor.lineX.set("visible", false);
        cursor.lineY.set("visible", false);

        series.appear(1000);
        chart.appear(1000, 80);

        return () => {
            root.dispose();
        };
    }, [activeRange, chartData]);

    const handleNext = () => {
        const nextIndex = (activeIndex + 1) % ranges.length;
        setActiveIndex(nextIndex);
        setActiveRange(ranges[nextIndex].range);
        setActiveWeeks(ranges[nextIndex].weeks);
    };

    const handlePrev = () => {
        const prevIndex = (activeIndex - 1 + ranges.length) % ranges.length;
        setActiveIndex(prevIndex);
        setActiveRange(ranges[prevIndex].range);
        setActiveWeeks(ranges[prevIndex].weeks);
    };

    return (
        <>

            <div className="bubble-carousel">
                <button onClick={handlePrev} className="btn btn-secondary bubble-carousel-prev">
                    &#8592;
                </button>
                <button onClick={handleNext} className="btn btn-secondary bubble-carousel-next">
                    &#8594;
                </button>
                <div className="mt-5">
                    <Row>
                        {activeWeeks.map((week, index) => (
                            <Col lg={3} key={index}>
                                <div className="weekly-bubble text-center">
                                    <h4>{week.week}</h4>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </div>
                <div id="bubble-chartdiv-yearly" style={{ width: "100%", height: "1616px" }}></div>
            </div>
        </>
    );
};

export default Yearly;
